import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MedicalInquiry, MedicalInquiryStatus } from '@omni/classes/medical-inquiry/medical-inquiry.class';
import { MedicalInquiryDataService } from '@omni/data-services/medical-inquiry/medical-inquiry.data.service';
import { MeetingStructureService } from '@omni/services/meeting-structure/meeting-structure.service';
import { DeviceService } from '@omni/services/device/device.service';
import { AlertService } from '@omni/services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectedSuggestionPillDataModel } from '@omni/models/search-config-data-model';
import { ActivityService } from '@omni/services/activity/activity.service';
import { MeetingActivityState } from '@omni/classes/activity/activity.class';
import { Events } from '@omni/events';

export const MI_TAB = {
  MY: 'myMedicalInquiries',
  TEAM: 'teamMedicalInquiries',
} as const;

@Injectable({
  providedIn: 'root'
})
export class MedicalInquiryService {
  selectedMI: MedicalInquiry | null = null;
  showNew = false;
  selectedTab: string = MI_TAB.MY;
  searchText = '';
  selectedMI$ = new BehaviorSubject<MedicalInquiry | null>(null);
  showNew$ = new BehaviorSubject<boolean>(false);
  searchEventSubject = new BehaviorSubject<string>('');
  selectedTab$ = new BehaviorSubject<string>(MI_TAB.MY);

  teamMedicalInquiries: MedicalInquiry[] = [];
  teamInquiriesLoading = false;

  /** Active filter pills from the ind-filter-menu. Cleared on tab switch or page exit. */
  selectedFilterData: SelectedSuggestionPillDataModel[] = [];

  /** True when new or edit form has unsaved changes. Used to show discard alert on navigate. */
  private _isFormDirty = false;

  /**
   * Agenda clone sets this before `setChildNavRoot` so auto-open edit does not depend on ion-nav
   * binding `@Input() isCloneFlow` (which can arrive after the first `_loadDisplayForm` run).
   * Cleared when edit opens or detail `ngOnDestroy`.
   */
  agendaCloneOpenEditAfterDisplay = false;

  /**
   * True while the in-meeting MI modal is open. Prevents `ActivitiesDetailsPane.ionViewWillEnter`
   * from overwriting the MI footer with the meeting footer — same pattern as
   * `caseManagementService.accessedFrom`, `sampleService.samplingDetailsViewMode`, etc.
   */
  isCreatingFromMeeting = false;

  constructor(
    private readonly alertService: AlertService,
    private readonly translate: TranslateService,
    private readonly device: DeviceService,
    private readonly medicalInquiryDataService: MedicalInquiryDataService,
    private readonly meetingStructureService: MeetingStructureService,
    private readonly activityService: ActivityService,
    private readonly events: Events
  ) {}

  /**
   * True if this MI is linked to a meeting (appointment). Uses `appointmentId`, which the model sets from
   * `_indskr_appointment_value` / `indskr_appointment` on construction — same for API and offline/Pouch payloads.
   * Call before scrap so unlink runs only when needed; read this before offline scrap mutates in-memory lists.
   */
  static hasMeetingLink(mi: MedicalInquiry | null | undefined): boolean {
    if (!mi) return false;
    const id = mi.appointmentId;
    return id != null && String(id).trim() !== '';
  }

  /**
   * A draft MI sitting on a meeting that has since been completed or cancelled must not be editable or
   * scrappable - a completed meeting takes no further changes. Every other embedded activity type closes
   * with its own record when the meeting completes, so its own state check covers this; an MI stays Draft,
   * so the host meeting has to be checked directly. Matches on offlineMeetingId too, because an MI created
   * before its meeting synced still references the offline id. Returns false for an unlinked MI, and when
   * the meeting is not loaded in `activities` (e.g. outside the agenda range).
   */
  isParentMeetingClosed(mi: MedicalInquiry | null | undefined): boolean {
    const appointmentId = mi?.appointmentId;
    if (!appointmentId) return false;
    const meeting = this.activityService.getActivityByID(appointmentId)
      ?? this.activityService.activities?.find(a => (a as { offlineMeetingId?: string }).offlineMeetingId === appointmentId);
    if (!meeting) return false;
    return meeting.state === MeetingActivityState.Completed || meeting.state === MeetingActivityState.Canceled;
  }

  /**
   * Shared MI scrap data path: deactivate (online API or offline queue), then unlink from meeting when linked.
   * Callers own confirmations, agenda removal, refresh events, toasts, and navigation.
   */
  async scrapMedicalInquiryCore(mi: MedicalInquiry): Promise<void> {
    if (!mi?.id) return;
    const hadMeetingLink = MedicalInquiryService.hasMeetingLink(mi);
    const isOnline = !this.device.isOffline;
    if (!isOnline) {
      await this.medicalInquiryDataService.scrapMedicalInquiryOffline(mi.id);
    } else {
      const response = await this.medicalInquiryDataService.updateMedicalInquiry(mi.id, {
        statecode: 1,
        statuscode: MedicalInquiryStatus.INACTIVE,
      });
      if (response) {
        this.medicalInquiryDataService.upsertMedicalInquiryFromApiResponse(response);
      } else {
        await this.medicalInquiryDataService.scrapMedicalInquiryOffline(mi.id);
      }
    }
    if (hadMeetingLink) {
      await this.meetingStructureService.removeMedicalInquiryMeetingLink(mi, isOnline);
    }
  }

  /**
   * Full scrap lifecycle: data deactivation + agenda list removal + calendar event + refresh.
   * Matches the CaseManagementService / EmailService pattern.
   * Callers own UI-specific cleanup (toasts, navigation, selectedActivity/RHS clearing).
   */
  async scrapMedicalInquiryAndSync(mi: MedicalInquiry): Promise<void> {
    await this.scrapMedicalInquiryCore(mi);
    await this.activityService.removeActivity(mi);
    this.activityService.publishActivityEvent({ action: 'Delete', activity: mi });
    this.activityService.finalizeAgendaRefresh();
  }

  /**
   * Sync an updated MI back into the agenda list and trigger a refresh.
   * Call after any successful save or submit so the agenda row reflects the latest state.
   */
  syncMedicalInquiryToAgenda(mi: MedicalInquiry): void {
    if (!mi) return;
    if (this.activityService.selectedActivity?.ID === mi.ID) this.activityService.selectedActivity = mi;
    this.activityService.updateActivities(mi);
    this.activityService.publishActivityEvent({ action: 'Update', activity: mi });
    if (MedicalInquiryService.hasMeetingLink(mi)) {
      this.events.publish('updateEmbeddedActivity', mi);
    }
    this.activityService.finalizeAgendaRefresh();
  }

  setSelectedMI(mi: MedicalInquiry | null) {
    this.selectedMI = mi;
    this.selectedMI$.next(mi);
  }

  setShowNew(value: boolean) {
    this.showNew = value;
    this.showNew$.next(value);
  }

  setFormDirty(value: boolean): void {
    this._isFormDirty = value;
  }

  get isFormDirty(): boolean {
    return this._isFormDirty;
  }

  /**
   * If form is dirty, shows discard confirmation. Returns true if navigation can proceed
   * (either not dirty or user confirmed discard).
   */
  async confirmDiscardIfDirty(): Promise<boolean> {
    if (!this._isFormDirty) return true;

    const result = await this.alertService.showAlert(
      {
        title: this.translate.instant('DISCARD_CHANGES'),
        message: this.translate.instant('CONTACT_R_U_WANT_CANCEL_LOSE_CHANGES'),
      },
      this.translate.instant('DISCARD'),
      this.translate.instant('CANCEL')
    );

    if (result?.role === 'ok') {
      this._isFormDirty = false;
      return true;
    }
    return false;
  }

  resetTeamState(): void {
    this.teamMedicalInquiries = [];
    this.teamInquiriesLoading = false;
    this.selectedFilterData = [];
  }

  resetFilters(): void {
    this.selectedFilterData = [];
  }

  /**
   * Applies active filter categories to a list.
   * OR logic within each category, AND logic across categories.
   */
  applyFilters(list: MedicalInquiry[]): MedicalInquiry[] {
    if (!this.selectedFilterData.length) return list;

    const filtersByCategory = new Map<string, string[]>();
    for (const pill of this.selectedFilterData) {
      const cat = pill.categoryPath;
      if (!filtersByCategory.has(cat)) filtersByCategory.set(cat, []);
      filtersByCategory.get(cat).push(pill.value);
    }

    return list.filter(mi => {
      for (const [category, values] of filtersByCategory) {
        let fieldValue: string;
        switch (category) {
          case 'caseType':
            fieldValue = mi.caseTypeFormatted ?? '';
            break;
          case 'contact':
            fieldValue = mi.contactId ?? '';
            break;
          case 'status':
            fieldValue = mi.statusCodeFormatted ?? '';
            break;
          case 'users':
            fieldValue = mi.ownerId ?? '';
            break;
          case 'product':
            // Products is a multi-value array — handle inline and skip the post-switch single-value check
            if (!mi.products?.some(p => values.includes(p.id))) return false;
            continue;
          default:
            continue;
        }
        if (!values.includes(fieldValue)) return false;
      }
      return true;
    });
  }

  clearSelection() {
    this.setSelectedMI(null);
    this.setShowNew(false);
    this._isFormDirty = false;
    this.resetTeamState();
    this.selectedTab = MI_TAB.MY;
    this.selectedTab$.next(MI_TAB.MY);
  }

  /**
   * Upsert or remove a linked entity payload entry in the given list.
   * Shared between create and edit components for managing pending product changes.
   * - New entity scrapped (addedNewDataId set, no existingId, statecode=1): removes from list
   * - Existing entry found: updates it
   * - Not found: appends
   */
  static upsertLinkEntityPayload(
    list: Array<{ linkedEntityId?: string; addedNewDataId?: string; values?: any; entity?: any; formStringValue?: any }>,
    linkEntity: any
  ): void {
    if (!linkEntity) return;
    const payload = {
      values: linkEntity.values,
      entity: linkEntity.entity,
      linkedEntityId: linkEntity.linkedEntityExistingDataId as string | undefined,
      addedNewDataId: linkEntity.linkedEntityAddedNewDataId as string | undefined,
      formStringValue: linkEntity.linkedEntityFormStringValue,
    };
    const idx = list.findIndex(x =>
      (payload.linkedEntityId && x.linkedEntityId === payload.linkedEntityId) ||
      (payload.addedNewDataId && x.addedNewDataId === payload.addedNewDataId)
    );
    if (idx >= 0) {
      if (payload.addedNewDataId && !payload.linkedEntityId && payload.values?.statecode === 1) {
        list.splice(idx, 1);
      } else {
        list[idx] = payload;
      }
    } else {
      list.push(payload);
    }
  }
}
